/**
 * Content Ownership Explorer — Admin Search Criteria + Results Grid (CCPL-8329).
 * Phase 1: criteria UI. Phase 2: lookup API + grid + pagination (CCPL-8360).
 * Never send `mode`.
 *
 * Security: React text escaping only (no dangerouslySetInnerHTML); strip control chars;
 * maxLength + autocomplete=off; controlled inputs; validate before fetch.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LiveUrlPagination from '../LiveUrlTracking/LiveUrlPagination';
import {
  fetchOwnership,
  displayOwner,
  DEFAULT_LIMIT
} from './contentOwnershipApi';
import { exportOwnershipToExcel } from '../../utils/contentOwnershipExportService';
import { EXPORT_PAGE_SIZE } from '../../utils/contentOwnershipExportUtils';
import './ContentOwnershipPanel.css';

const PATH_PLACEHOLDER = 'RootFolder/[subfolder1]/[Subfolder2]/....';

const DEFAULT_FILTERS = Object.freeze({
  path: '',
  type: 'all',
  extension: Object.freeze([]),
  /** Dropdown value: '0'..'10' or 'custom' */
  level: '0',
  /** Used only when level === 'custom' */
  customLevel: ''
});

const OBJECT_TYPES = Object.freeze([
  { value: 'all', label: 'All' },
  { value: 'file', label: 'File' },
  { value: 'folder', label: 'Folder' }
]);

/** Labels match Live URL Tracking File Types casing; values stay API extension tokens. */
const EXTENSIONS = Object.freeze([
  { value: 'php', label: 'PHP' },
  { value: 'pdf', label: 'PDF' },
  { value: 'html', label: 'HTML' },
  { value: 'htm', label: 'HTM' },
  { value: 'css', label: 'CSS' },
  { value: 'js', label: 'JS' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
  { value: 'txt', label: 'TXT' },
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'jpeg', label: 'JPEG' },
  { value: 'gif', label: 'GIF' },
  { value: 'svg', label: 'SVG' },
  { value: 'zip', label: 'ZIP' },
  { value: 'mp4', label: 'MP4' },
  { value: 'mp3', label: 'MP3' },
  { value: 'unlisted', label: 'Unlisted' }
]);

/** Concrete extensions used to resolve Unlisted (excludes Unlisted token). */
export const KNOWN_OWNERSHIP_FILE_EXTENSIONS = Object.freeze(
  EXTENSIONS.map((option) => option.value).filter((value) => value !== 'unlisted')
);

const PRESET_LEVEL_MAX = 10;
/** Backend/FE shared hard cap for custom Levels To Scan. */
export const MAX_CUSTOM_LEVEL = 50;

const LEVEL_OPTIONS = Object.freeze([
  ...Array.from({ length: PRESET_LEVEL_MAX + 1 }, (_, level) => ({
    value: String(level),
    label: String(level)
  })),
  { value: 'custom', label: 'Custom' }
]);

const EMPTY_PAGINATION = Object.freeze({
  currentPage: 1,
  totalPages: 0,
  totalRecords: 0,
  pageSize: DEFAULT_LIMIT
});

/** Remove C0/C1 control chars (incl. null) that scanners treat as injection vectors. */
function sanitizePathInput(rawValue) {
  return String(rawValue ?? '').replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
}

function normalizePathForSearch(rawPath) {
  return sanitizePathInput(rawPath).trim().replace(/^\/+|\/+$/g, '');
}

/**
 * Resolve dropdown + optional custom input into the API `level` value.
 * @returns {{ ok: true, level: string } | { ok: false, message: string }}
 */
function resolveSearchLevel(filters = {}) {
  if (filters.level === 'custom') {
    const raw = String(filters.customLevel ?? '').trim();
    if (!raw) {
      return {
        ok: false,
        message: `Enter a custom level between 0 and ${MAX_CUSTOM_LEVEL}.`
      };
    }
    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_CUSTOM_LEVEL) {
      return {
        ok: false,
        message: `Custom level must be an integer between 0 and ${MAX_CUSTOM_LEVEL}.`
      };
    }
    return { ok: true, level: String(parsed) };
  }

  const preset = Number(filters.level);
  if (!Number.isInteger(preset) || preset < 0 || preset > PRESET_LEVEL_MAX) {
    return { ok: false, message: 'Select a valid Levels To Scan value.' };
  }
  return { ok: true, level: String(preset) };
}

function rowKey(item, index) {
  const path = typeof item?.relativePath === 'string' ? item.relativePath : '';
  return path || `row-${index}`;
}

/** Safe clipboard text: strip control chars; never copy HTML. */
function toClipboardPath(relativePath) {
  return sanitizePathInput(relativePath).trim();
}

const ContentOwnershipPanel = () => {
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [pathError, setPathError] = useState('');
  const [levelError, setLevelError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(() => ({ ...EMPTY_PAGINATION }));
  const [infoNote, setInfoNote] = useState('');
  const [emptyMessageFromApi, setEmptyMessageFromApi] = useState('');
  const [truncatedMessage, setTruncatedMessage] = useState('');
  const [requestError, setRequestError] = useState('');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [copyStatusByPath, setCopyStatusByPath] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportProgress, setExportProgress] = useState(null);
  const copyTimeoutsRef = useRef({});
  const allItemsRef = useRef([]);

  useEffect(() => {
    const timeouts = copyTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
    };
  }, []);

  const extensionDisabled = filters.type === 'folder';
  const isCustomLevel = filters.level === 'custom';
  const levelOptions = useMemo(() => LEVEL_OPTIONS, []);
  const controlsDisabled = loading || isExporting;
  const isExportDisabled = controlsDisabled || !appliedFilters || pagination.totalRecords === 0;

  const applyClientPagination = useCallback((records, page, pageSize) => {
    const list = Array.isArray(records) ? records : [];
    const totalRecords = list.length;
    const totalPages = totalRecords === 0 ? 0 : Math.ceil(totalRecords / pageSize);
    const safePage =
      totalPages === 0 ? 1 : Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * pageSize;
    allItemsRef.current = list;
    setAllItems(list);
    setItems(list.slice(start, start + pageSize));
    setPagination({
      currentPage: safePage,
      totalPages,
      totalRecords,
      pageSize
    });
  }, []);

  const clearSearchErrors = () => {
    setPathError('');
    setLevelError('');
    setRequestError('');
    setExportError('');
    setFeatureUnavailable(false);
    setTruncatedMessage('');
  };

  const updateFilter = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'type' && value === 'folder') {
        next.extension = [];
      }
      if (field === 'path') {
        next.path = sanitizePathInput(value);
      }
      if (field === 'level') {
        if (value !== 'custom') {
          next.customLevel = '';
        }
      }
      if (field === 'customLevel') {
        // Digits only; allow empty while typing
        next.customLevel = String(value ?? '').replace(/[^\d]/g, '');
      }
      return next;
    });
    if (field === 'path') {
      setPathError('');
    }
    if (field === 'level' || field === 'customLevel') {
      setLevelError('');
    }
  };

  const handleFileTypeChange = (fileType) => {
    if (extensionDisabled || controlsDisabled) {
      return;
    }
    setFilters((prev) => {
      const current = Array.isArray(prev.extension) ? prev.extension : [];
      const updated = current.includes(fileType)
        ? current.filter((type) => type !== fileType)
        : [...current, fileType];
      return { ...prev, extension: updated };
    });
  };

  const clearResults = () => {
    allItemsRef.current = [];
    setAllItems([]);
    setItems([]);
    setPagination({ ...EMPTY_PAGINATION });
    setInfoNote('');
    setEmptyMessageFromApi('');
    setTruncatedMessage('');
    setRequestError('');
    setFeatureUnavailable(false);
    setCopyStatusByPath({});
  };

  const handleCopyPath = async (relativePath) => {
    const text = toClipboardPath(relativePath);
    if (!text) {
      return;
    }
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        return;
      }
      await navigator.clipboard.writeText(text);
      setCopyStatusByPath((prev) => ({ ...prev, [text]: 'copied' }));
      if (copyTimeoutsRef.current[text]) {
        clearTimeout(copyTimeoutsRef.current[text]);
      }
      copyTimeoutsRef.current[text] = setTimeout(() => {
        setCopyStatusByPath((prev) => {
          const next = { ...prev };
          delete next[text];
          return next;
        });
      }, 2000);
    } catch {
      // Clipboard may be denied; avoid logging path contents (PII/path disclosure).
    }
  };

  const handleReset = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setAppliedFilters(null);
    setPathError('');
    setLevelError('');
    setHasSearched(false);
    setLoading(false);
    setIsExporting(false);
    setExportError('');
    setExportProgress(null);
    clearResults();
  };

  const runFetch = useCallback(async (filterSnapshot, pageSize = DEFAULT_LIMIT) => {
    setLoading(true);
    setRequestError('');
    setFeatureUnavailable(false);
    // Clear previous Results Grid chrome immediately so only the loading message shows
    allItemsRef.current = [];
    setAllItems([]);
    setItems([]);
    setInfoNote('');
    setEmptyMessageFromApi('');
    setTruncatedMessage('');
    setCopyStatusByPath({});
    setPagination({ ...EMPTY_PAGINATION, pageSize });

    const queryBase = {
      path: filterSnapshot.path,
      type: filterSnapshot.type,
      extension: filterSnapshot.type === 'folder' ? 'all' : filterSnapshot.extension,
      level: filterSnapshot.level
    };

    const first = await fetchOwnership({
      ...queryBase,
      page: 1,
      limit: EXPORT_PAGE_SIZE
    });

    if (!first.ok) {
      setLoading(false);
      allItemsRef.current = [];
      setAllItems([]);
      setItems([]);
      setPagination({ ...EMPTY_PAGINATION, pageSize });
      setInfoNote('');
      setEmptyMessageFromApi('');
      setTruncatedMessage('');
      if (first.status === 503) {
        setFeatureUnavailable(true);
        setRequestError(first.message || 'Content ownership lookup is unavailable.');
      } else {
        setFeatureUnavailable(false);
        setRequestError(first.message || 'Request failed');
      }
      return;
    }

    const records = [...(first.data.items || [])];
    const reportedTotal = Number(first.data.pagination?.totalRecords);
    const totalPages =
      first.data.pagination?.totalPages
      || (records.length ? 1 : 0);

    // Only page the server when the first response did not already include every record.
    const needsMorePages =
      Number.isFinite(reportedTotal) && reportedTotal > records.length && totalPages > 1;

    if (needsMorePages) {
      for (let page = 2; page <= totalPages; page += 1) {
        const next = await fetchOwnership({
          ...queryBase,
          page,
          limit: EXPORT_PAGE_SIZE
        });
        if (!next.ok) {
          setLoading(false);
          allItemsRef.current = [];
          setAllItems([]);
          setItems([]);
          setPagination({ ...EMPTY_PAGINATION, pageSize });
          setInfoNote('');
          setEmptyMessageFromApi('');
          setTruncatedMessage('');
          setFeatureUnavailable(next.status === 503);
          setRequestError(next.message || 'Request failed');
          return;
        }
        records.push(...(next.data.items || []));
      }
    }

    setLoading(false);
    setFeatureUnavailable(false);
    setRequestError('');
    setInfoNote(first.data.infoNote || '');
    setEmptyMessageFromApi(first.data.emptyMessage || '');
    setTruncatedMessage(
      first.data.truncated
        ? first.data.truncatedMessage ||
            'Scan timed out or reached the item cap. Lower the level or apply filters and search again.'
        : ''
    );
    applyClientPagination(records, 1, pageSize);
  }, [applyClientPagination]);

  const handleSearch = async (event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    // Clear prior validation / request / export errors on every Search click
    clearSearchErrors();

    const path = normalizePathForSearch(filters.path);
    if (!path) {
      setPathError('Path is required.');
      setHasSearched(false);
      return;
    }

    const levelResult = resolveSearchLevel(filters);
    if (!levelResult.ok) {
      setLevelError(levelResult.message);
      setHasSearched(false);
      return;
    }

    const snapshot = {
      path,
      type: filters.type,
      extension: filters.type === 'folder' ? [] : [...(filters.extension || [])],
      level: levelResult.level
    };

    setHasSearched(true);
    setAppliedFilters(snapshot);
    await runFetch(snapshot, pagination.pageSize || DEFAULT_LIMIT);
  };

  const getExportButtonLabel = () => {
    if (isExporting) {
      return 'Exporting...';
    }
    return 'Export';
  };

  const handleExport = () => {
    if (!appliedFilters || isExporting || loading || pagination.totalRecords === 0) {
      return;
    }

    const records = allItemsRef.current;
    if (!Array.isArray(records) || records.length === 0) {
      setExportError('No records to export');
      return;
    }

    setIsExporting(true);
    setExportError('');
    setExportProgress(null);

    try {
      // Reuse the cached Search results already shown in the grid — no FSx re-fetch.
      exportOwnershipToExcel(records, appliedFilters);
    } catch (error) {
      setExportError(error?.message || 'Export failed');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  /** Page / page-size changes must never call the lookup API — only slice the cached result set. */
  const handlePageChange = (page) => {
    if (!appliedFilters || loading || isExporting) {
      return;
    }
    applyClientPagination(allItemsRef.current, page, pagination.pageSize || DEFAULT_LIMIT);
  };

  const handlePageSizeChange = (pageSize) => {
    if (!appliedFilters || loading || isExporting) {
      return;
    }
    const nextSize = Number(pageSize);
    if (!Number.isFinite(nextSize) || nextSize < 1) {
      return;
    }
    applyClientPagination(allItemsRef.current, 1, nextSize);
  };

  const showEmptyPlaceholder = !loading && hasSearched && items.length === 0 && !requestError;
  const showInitialHint = !hasSearched && !loading;

  let emptyMessage = 'Enter a path and Search';
  if (hasSearched && !requestError) {
    emptyMessage =
      emptyMessageFromApi || 'No ownership records found for this criteria.';
  }

  return (
    <div className="content-ownership-panel" data-testid="content-ownership-panel">
      <section
        className="content-ownership-filters"
        aria-label="Search Criteria"
        data-testid="content-ownership-filters"
      >
        <h3
          className="content-ownership-criteria-title"
          data-testid="content-ownership-search-criteria-title"
        >
          Search Criteria
        </h3>

        <form
          className="content-ownership-filters-form"
          onSubmit={handleSearch}
          noValidate
          data-testid="content-ownership-filters-form"
        >
          <div className="content-ownership-filter-group content-ownership-filter-full">
            <label htmlFor="content-ownership-path-input">
              Full Path <span className="content-ownership-required" aria-hidden="true">*</span>
            </label>
            <input
              id="content-ownership-path-input"
              data-testid="content-ownership-path-input"
              type="text"
              name="contentOwnershipPath"
              value={filters.path}
              onChange={(event) => updateFilter('path', event.target.value)}
              placeholder={PATH_PLACEHOLDER}
              required
              maxLength={512}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={Boolean(pathError)}
              aria-describedby={pathError ? 'content-ownership-path-error' : undefined}
              disabled={controlsDisabled}
            />
            {pathError ? (
              <p
                id="content-ownership-path-error"
                className="content-ownership-error"
                data-testid="content-ownership-path-error"
                role="alert"
              >
                {pathError}
              </p>
            ) : null}
          </div>

          <div className="content-ownership-filters-grid">
            <div className="content-ownership-filter-group">
              <label htmlFor="content-ownership-type-select">Object Type</label>
              <select
                id="content-ownership-type-select"
                data-testid="content-ownership-type-select"
                value={filters.type}
                onChange={(event) => updateFilter('type', event.target.value)}
                disabled={controlsDisabled}
              >
                {OBJECT_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="content-ownership-filter-group">
              <label htmlFor="content-ownership-level-select">Levels To Scan</label>
              <select
                id="content-ownership-level-select"
                data-testid="content-ownership-level-select"
                value={filters.level}
                onChange={(event) => updateFilter('level', event.target.value)}
                disabled={controlsDisabled}
                aria-invalid={Boolean(levelError)}
                aria-describedby={
                  levelError
                    ? 'content-ownership-level-error'
                    : isCustomLevel
                      ? 'content-ownership-custom-level-hint'
                      : undefined
                }
              >
                {levelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {isCustomLevel ? (
                <div className="content-ownership-custom-level">
                  <label htmlFor="content-ownership-custom-level-input" className="content-ownership-sr-only">
                    Custom level
                  </label>
                  <input
                    id="content-ownership-custom-level-input"
                    data-testid="content-ownership-custom-level-input"
                    type="text"
                    inputMode="numeric"
                    name="contentOwnershipCustomLevel"
                    value={filters.customLevel}
                    onChange={(event) => updateFilter('customLevel', event.target.value)}
                    placeholder={`0–${MAX_CUSTOM_LEVEL}`}
                    maxLength={2}
                    autoComplete="off"
                    disabled={controlsDisabled}
                    aria-invalid={Boolean(levelError)}
                  />
                  <span
                    id="content-ownership-custom-level-hint"
                    className="content-ownership-custom-level-hint"
                  >
                    Enter 0–{MAX_CUSTOM_LEVEL}
                  </span>
                </div>
              ) : null}
              {levelError ? (
                <p
                  id="content-ownership-level-error"
                  className="content-ownership-error"
                  data-testid="content-ownership-level-error"
                  role="alert"
                >
                  {levelError}
                </p>
              ) : null}
            </div>
          </div>

          <div className="content-ownership-filter-group content-ownership-filter-file-types">
            <label id="content-ownership-extension-label">File Types</label>
            <div
              className="content-ownership-file-type-multiselect"
              data-testid="content-ownership-extension-select"
              role="group"
              aria-labelledby="content-ownership-extension-label"
              aria-disabled={extensionDisabled || controlsDisabled}
            >
              {EXTENSIONS.map((option) => (
                <label key={option.value} className="content-ownership-checkbox-label">
                  <input
                    type="checkbox"
                    data-testid={`content-ownership-extension-${option.value}`}
                    checked={
                      Array.isArray(filters.extension)
                      && filters.extension.includes(option.value)
                    }
                    onChange={() => handleFileTypeChange(option.value)}
                    disabled={extensionDisabled || controlsDisabled}
                    aria-label={option.label}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          <div className="content-ownership-actions">
            <button
              type="submit"
              className="content-ownership-btn content-ownership-btn-primary"
              data-testid="content-ownership-search-btn"
              disabled={controlsDisabled}
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
            <button
              type="button"
              className="content-ownership-btn content-ownership-btn-secondary"
              data-testid="content-ownership-reset-btn"
              onClick={handleReset}
              disabled={controlsDisabled}
            >
              Reset
            </button>
            <button
              type="button"
              className={`content-ownership-btn ${
                isExportDisabled
                  ? 'content-ownership-btn-secondary'
                  : 'content-ownership-btn-primary'
              }`}
              data-testid="content-ownership-export-btn"
              disabled={isExportDisabled}
              onClick={handleExport}
              aria-label="Export content ownership results to Excel"
            >
              {getExportButtonLabel()}
            </button>
          </div>
          {exportError ? (
            <p
              className="content-ownership-error"
              data-testid="content-ownership-export-error"
              role="alert"
            >
              {exportError}
            </p>
          ) : null}
        </form>
      </section>

      <section
        className="content-ownership-results"
        aria-label="Results Grid"
        data-testid="content-ownership-results"
      >
        <h3 className="content-ownership-results-title" data-testid="content-ownership-results-title">
          Results Grid
        </h3>

        {featureUnavailable ? (
          <div
            className="content-ownership-banner content-ownership-banner-unavailable"
            data-testid="content-ownership-unavailable"
            role="alert"
          >
            {requestError}
          </div>
        ) : null}

        {!featureUnavailable && requestError ? (
          <div
            className="content-ownership-error"
            data-testid="content-ownership-request-error"
            role="alert"
          >
            {requestError}
          </div>
        ) : null}

        {truncatedMessage && !loading ? (
          <div
            className="content-ownership-banner content-ownership-banner-truncated"
            data-testid="content-ownership-truncated-banner"
            role="status"
          >
            {truncatedMessage}
          </div>
        ) : null}

        {infoNote && !loading ? (
          <p className="content-ownership-info-note" data-testid="content-ownership-info-note">
            {infoNote}
          </p>
        ) : null}

        {loading ? (
          <div data-testid="content-ownership-loading" className="content-ownership-loading" role="status">
            Loading ownership records…
          </div>
        ) : null}

        {(showInitialHint || showEmptyPlaceholder) && !loading ? (
          <div
            className="content-ownership-empty-state"
            data-testid="content-ownership-empty-state"
            role="status"
          >
            {emptyMessage}
          </div>
        ) : null}

        {!loading && items.length > 0 ? (
          <div className="content-ownership-table-wrap" data-testid="content-ownership-table-wrap">
            <table className="content-ownership-table" data-testid="content-ownership-table">
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">Name</th>
                  <th scope="col">Create Owner</th>
                  <th scope="col">Modify Owner</th>
                  <th scope="col">Path</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const pathText = toClipboardPath(item.relativePath);
                  return (
                  <tr key={rowKey(item, index)} data-testid="content-ownership-row">
                    <td>{item.objectType || (item.isFile ? 'File' : 'Folder')}</td>
                    <td>{item.name || '—'}</td>
                    <td>{displayOwner(item.createdOwner)}</td>
                    <td>{displayOwner(item.modifiedOwner)}</td>
                    <td>
                      <div className="content-ownership-path-cell">
                        <span className="content-ownership-path-text" title={pathText || undefined}>
                          {pathText || '—'}
                        </span>
                        {pathText ? (
                          <button
                            type="button"
                            className="content-ownership-copy-btn"
                            data-testid="content-ownership-copy-path-btn"
                            aria-label={`Copy path ${pathText}`}
                            title="Copy Path"
                            onClick={() => handleCopyPath(pathText)}
                            tabIndex={0}
                          >
                            📋
                          </button>
                        ) : null}
                        {pathText && copyStatusByPath[pathText] === 'copied' ? (
                          <span
                            className="content-ownership-copy-success"
                            data-testid="content-ownership-copy-success"
                            role="status"
                          >
                            Copied!
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {!loading ? (
          <div data-testid="content-ownership-pagination">
            <LiveUrlPagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default ContentOwnershipPanel;

export {
  DEFAULT_FILTERS,
  PATH_PLACEHOLDER,
  sanitizePathInput,
  normalizePathForSearch,
  resolveSearchLevel
};
